import { EDeliverableStatus } from '@constants';
import { Flex } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  NoPendingTasksWrapper,
  PendingTasksWrapper,
  ShowMoreButton,
  WidgetTitle,
  WidgetWrapper
} from '../shared/WidgetShared.styles';

import { DeliverableItem } from './components/DeliverableItem';
import { EmptyDeliverables } from './components/EmptyDeliverables/EmptyDeliverables';
import { DeliverablesList } from './DeliverablesWidget.styles';

import { LoadingCircleSpinner } from '@/components/Loaders';
import { useStudentDeliverables } from '@/store';

export const DeliverablesWidget: React.FC = () => {
  const { t } = useTranslation('StudentDashboardTranslations');
  const [showMore, setShowMore] = useState(false);
  const { deliverables, isLoading } = useStudentDeliverables();

  const completedDeliverables = useMemo(() => {
    if (deliverables && !isLoading) {
      return deliverables.filter(deliverable => deliverable.status === EDeliverableStatus.COMPLETED)
        .length;
    }
    return 0;
  }, [deliverables, isLoading]);

  const sortedDeliverables = useMemo(() => {
    if (deliverables && !isLoading) {
      return deliverables.sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    }
    return [];
  }, [deliverables, isLoading]);

  const displayedDeliverables = showMore ? sortedDeliverables : sortedDeliverables.slice(0, 5);

  return (
    <WidgetWrapper>
      <Flex gap={8} align="center">
        <WidgetTitle>{t('DELIVERABLES_TITLE')}</WidgetTitle>
        {sortedDeliverables?.length === 0 ? (
          <NoPendingTasksWrapper>0</NoPendingTasksWrapper>
        ) : (
          <PendingTasksWrapper>
            {t('COMPLETED_TASKS', {
              completed: completedDeliverables,
              total: sortedDeliverables?.length
            })}
          </PendingTasksWrapper>
        )}
      </Flex>
      {isLoading ? (
        <LoadingCircleSpinner />
      ) : sortedDeliverables.length === 0 ? (
        <EmptyDeliverables />
      ) : (
        <DeliverablesList>
          {displayedDeliverables.map(deliverable => (
            <DeliverableItem key={deliverable._id} deliverable={deliverable} />
          ))}
          {sortedDeliverables.length > 5 && (
            <ShowMoreButton type="text" onClick={() => setShowMore(!showMore)}>
              {showMore ? t('SHOW_LESS') : t('SHOW_MORE')}
            </ShowMoreButton>
          )}
        </DeliverablesList>
      )}
    </WidgetWrapper>
  );
};
